import React from 'react';
import { styled } from '../../../../../packages/theme/stitches.config';
import { GenericLayout } from '../../../../../packages/constants/generic';
import { IMatchPlayerLineup, ISubscriberInfo } from '../../../../../packages/types';
import LineupsPlayer from './LineupsPlayer'; // Assuming LineupsPlayer is in the same directory

// --- TeamFormationDisplay Component ---
interface TeamFormationDisplayProps {
  teamType: 'home' | 'away';
  formationData: Record<number, IMatchPlayerLineup[]>;
  mode: GenericLayout;
  fieldWidth: number;
  borderColor: string;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
}

export const TeamFormationDisplay = ({
  teamType,
  formationData,
  mode,
  fieldWidth,
  borderColor,
  handleClick,
}: TeamFormationDisplayProps) => {
  let displayEntries = Object.entries(formationData);

  /**
   * The players from the away team need to be reversed in both X coordinates and Y coordinates before displaying
   * because they must 'mirror' the home team on the field.
   * slice() is used because the original list is displayed in the 'Starting lineups' tab.
   */
  if (teamType === 'away') {
    displayEntries = displayEntries.reverse().map(([position, playerLineups]) => {
      return [position, playerLineups.slice().reverse()];
    });
  }

  const calculatedNumberOfPositions = displayEntries.length; // Use displayEntries length directly

  return (
    <TeamContainer
      teamType={teamType}
      mode={mode}
      // Pass the count as a CSS variable for the grid layout
      style={{ '--position-count': displayEntries.length } as React.CSSProperties}
    >
      {displayEntries.map(([position, playerLineups]) => (
        <PositionContainer
          key={position}
          mode={mode}
          // Apply 'start' alignment only for the away team's goalkeeper in vertical modestion logic
          start={position === '1' && mode !== GenericLayout.HORIZONTAL && teamType === 'away'}
        >
          {playerLineups.map((playerLineup) => (
            <LineupsPlayer
              key={playerLineup.player.id}
              playerLineup={playerLineup}
              numberOfPlayersWithSamePosition={playerLineups.length}
              numberOfPositions={calculatedNumberOfPositions} // Use calculated value
              // Map VERTICAL_CONDENSED to VERTICAL for LineupsPlayer
              alignmentInContainer={mode === GenericLayout.VERTICAL_CONDENSED ? GenericLayout.VERTICAL : mode}
              fieldWidth={fieldWidth}
              borderColor={borderColor}
              handleClick={handleClick}
            />
          ))}
        </PositionContainer>
      ))}
    </TeamContainer>
  );
};

// --- Styled Components ---
const TeamContainer = styled.withConfig({ componentId: 'TeamFormationDisplay', displayName: 'TeamContainer' })('div', {
  display: 'grid',
  position: 'absolute',

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {
        gridTemplateColumns: 'repeat(var(--position-count, 1), 1fr)', // Use CSS var for columns
        width: '50%',
        height: '100%',
      },
      [GenericLayout.VERTICAL]: {
        gridTemplateRows: 'repeat(var(--position-count, 1), 1fr)', // Use CSS var for rows
        width: '100%',
        height: '50%',
      },
      [GenericLayout.VERTICAL_CONDENSED]: {
        gridTemplateRows: 'repeat(var(--position-count, 1), 1fr)', // Use CSS var for rows
        width: '100%',
        height: '50%',
      },
    },
    teamType: {
      home: {}, // Base styles specific to home if needed in future
      away: {}, // Base styles specific to away if needed in future
    },
  },
  compoundVariants: [
    // Positioning based on mode and teamType
    { mode: GenericLayout.HORIZONTAL, teamType: 'home', css: { left: 0 } },
    { mode: GenericLayout.HORIZONTAL, teamType: 'away', css: { right: 0 } },
    { mode: GenericLayout.VERTICAL, teamType: 'home', css: { top: 0 } },
    { mode: GenericLayout.VERTICAL, teamType: 'away', css: { bottom: 0 } },
    { mode: GenericLayout.VERTICAL_CONDENSED, teamType: 'home', css: { top: 0 } },
    { mode: GenericLayout.VERTICAL_CONDENSED, teamType: 'away', css: { bottom: 0 } },
  ],
});

const PositionContainer = styled.withConfig({ componentId: 'TeamFormationDisplay', displayName: 'PositionContainer' })('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-evenly',

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {
        flexDirection: 'column-reverse', // Horizontal mode stacks players vertically
      },
      [GenericLayout.VERTICAL]: {
        flexDirection: 'row', // Vertical modes stack players horizontally
      },
      [GenericLayout.VERTICAL_CONDENSED]: {
        flexDirection: 'row', // Vertical modes stack players horizontally
      },
    },
    start: {
      // Keep this variant for specific alignment needs (e.g., goalkeeper in vertical)
      true: {
        alignItems: 'flex-start', // Use flex-start for consistency
      },
      false: {},
    },
  },
});
